import { FitbitAlertState, FitbitMetric, FitbitReadParticipant, FitbitSummary, readFitbitAlertStates, readFitbitData } from '../../../services/fitbitCollectionDataService';
import { DateRange, datesInRange } from './dataAvailability';

export type AnalysisRow = { participant: FitbitReadParticipant; summary?: FitbitSummary; states?: FitbitAlertState[]; failed?: boolean; decisionsFailed?: boolean };
export type CoverageState = 'recorded' | 'empty' | 'unknown';
export const thresholdLabels: Record<string, string> = {
  steps_total: 'Steps (steps/day)', hr_coverage_hours: 'Heart-rate coverage (h/day)', calories_total: 'Total calories (kcal/day)',
  distance_total: 'Distance (km/day)', sleep_duration: 'Sleep (min/day)', breathing_rate_avg: 'Breathing rate (breaths/min)',
  hrv_rmssd: 'HRV RMSSD (ms)', hrv_rmssd_cv: 'HRV variation (%)', skin_temperature: 'Skin temperature (°C)',
  core_temperature: 'Core temperature (°C)', spo2_avg: 'Blood oxygen (%)', water_intake: 'Water intake (mL/day)',
  active_minutes_light: 'Light activity (min/day)', active_minutes_moderate: 'Moderate activity (min/day)', active_minutes_vigorous: 'Vigorous activity (min/day)',
};
export function checkedDay(summary: FitbitSummary, date: string) {
  return summary.checkedWindows.some(window => window.start <= date && date < window.endExclusive);
}
const coverageIndexes = new WeakMap<FitbitSummary, Map<string, Map<string, FitbitSummary['dailyCoverage'][number]>>>();
function coverageIndex(summary: FitbitSummary) {
  let index = coverageIndexes.get(summary);
  if (!index) {
    index = new Map();
    for (const row of summary.dailyCoverage) {
      const route = index.get(row.routeKey) || new Map(); route.set(row.date, row); index.set(row.routeKey, route);
    }
    coverageIndexes.set(summary, index);
  }
  return index;
}
export function metricCoverage(summary: FitbitSummary, metric: FitbitMetric, dates: string[], includeDays = true) {
  const values = coverageIndex(summary).get(metric.routeKey);
  const days: Array<{ date: string; records: number; absent: number; checked: boolean; state: CoverageState }> = [];
  let recordedDays = 0, emptyDays = 0, unknownDays = 0, records = 0;
  let firstDate: string | null = null, lastDate: string | null = null;
  for (const date of dates) {
    const row = values?.get(date);
    const count = Number(row?.presentRecords || 0);
    const checked = checkedDay(summary, date);
    const state: CoverageState = count > 0 ? 'recorded' : checked ? 'empty' : 'unknown';
    if (state === 'recorded') { recordedDays++; firstDate ||= date; lastDate = date; }
    else if (state === 'empty') emptyDays++;
    else unknownDays++;
    records += count;
    if (includeDays) days.push({ date, records: count, absent: Number(row?.absentRecords || 0), checked, state });
  }
  return { metric, days, recordedDays, emptyDays, unknownDays, records, firstDate, lastDate,
    coverage: dates.length && (recordedDays || unknownDays < dates.length) ? recordedDays / dates.length * 100 : null };
}
export function missingRanges(dates: string[]) {
  const ranges: Array<{ start: string; end: string; days: number }> = [];
  for (const date of Array.from(new Set(dates)).sort()) {
    const previous = ranges[ranges.length - 1];
    if (previous && Date.parse(date) - Date.parse(previous.end) === 86400000) { previous.end = date; previous.days++; }
    else ranges.push({ start: date, end: date, days: 1 });
  }
  return ranges;
}
export function periods(dates: string[], aggregation: 'daily' | 'weekly' | 'monthly' | 'yearly') {
  const groups = new Map<string, string[]>();
  for (const date of dates) {
    const day = new Date(`${date}T00:00:00Z`);
    const monday = new Date(day.getTime() - ((day.getUTCDay() + 6) % 7) * 86400000).toISOString().slice(0, 10);
    const key = aggregation === 'yearly' ? date.slice(0, 4) : aggregation === 'monthly' ? date.slice(0, 7) : aggregation === 'weekly' ? monday : date;
    groups.set(key, [...(groups.get(key) || []), date]);
  }
  return Array.from(groups).map(([label, days]) => ({ label, dates: days }));
}
export const scoredDays = (row: AnalysisRow) => row.summary?.adherence.days.filter(day => day.checked && day.completeDay && day.adherence !== null) || [];
export const average = (values: number[]) => values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
export function studyAdherence(rows: AnalysisRow[], dates: string[]) {
  const days = rows.map(row => new Map(scoredDays(row).map(day => [day.date, day.adherence!])));
  return dates.map(date => { const values = days.flatMap(map => map.has(date) ? [map.get(date)!] : []);
    return { date, value: average(values), participants: values.length, belowTarget: values.filter(value => value < 70).length };
  });
}
export function alertState(row: AnalysisRow, date: string): FitbitAlertState {
  return row.states?.find(state => state.date === date) || { date, status: 'pending', version: 0, snoozedUntil: null, lastContactAt: null, contact: null, history: [] };
}
export function effectiveStatus(state: FitbitAlertState, now = new Date().toISOString()) {
  return state.status === 'snoozed' && (!state.snoozedUntil || state.snoozedUntil <= now) ? 'pending' : state.status;
}
export function participantAlerts(row: AnalysisRow) {
  const days = new Map((row.summary?.adherence.days || []).map(day => [day.date, day]));
  const dateKeys = new Set([...scoredDays(row).filter(day => day.adherence! < 70).map(day => day.date), ...(row.states || []).map(state => state.date)]);
  return Array.from(dateKeys).sort().reverse().map(date => ({ date, day: days.get(date), state: alertState(row, date),
    active: !!days.get(date)?.checked && !!days.get(date)?.completeDay && days.get(date)?.adherence != null && days.get(date)!.adherence! < 70 }));
}
export async function loadAnalysis(studyId: string, participants: FitbitReadParticipant[], range: DateRange, decisions: boolean,
  signal: AbortSignal, progress: (count: number) => void,
  readers = { readFitbitData, readFitbitAlertStates }): Promise<AnalysisRow[]> {
  const rows: AnalysisRow[] = [];
  for (let offset = 0; offset < participants.length && !signal.aborted; offset += 3) {
    const batch = await Promise.all(participants.slice(offset, offset + 3).map(async participant => {
      if (participant.enrollmentStatus !== 'completed') return { participant };
      try {
        const summary = await readers.readFitbitData<FitbitSummary>(studyId, participant.participantId, 'summary', range);
        if (signal.aborted) return { participant };
        if (!decisions) return { participant, summary };
        try { const { states } = await readers.readFitbitAlertStates(studyId, participant.participantId, range); return { participant, summary, states }; }
        catch { return { participant, summary, decisionsFailed: true }; }
      } catch { return { participant, failed: true }; }
    }));
    if (signal.aborted) break;
    rows.push(...batch); progress(rows.length);
  }
  return rows;
}
export function analysisReport(rows: AnalysisRow[], catalog: FitbitMetric[], range: DateRange) {
  const dates = datesInRange(range);
  return { generatedAt: new Date().toISOString(), range,
    definitions: { coverage: 'Days with any present record / selected days. Unchecked days are separately identified; presence is not proof of wear.',
      adherence: 'Mean of capped study-threshold percentages on collected, completed UTC days. Uncollected, incomplete or unsupported days remain unknown. Missing measurements on checked complete days score zero for that target.' },
    participants: rows.map(row => ({ participant: row.participant, failed: !!row.failed, decisionsUnavailable: !!row.decisionsFailed,
      coverage: row.summary ? catalog.map(metric => {
        const { days, ...counts } = metricCoverage(row.summary!, metric, dates, false); return counts;
      }) : null,
      dailyCoverage: row.summary?.dailyCoverage || null, checkedWindows: row.summary?.checkedWindows || null,
      thresholds: row.summary?.adherence.thresholds || null, adherence: row.summary?.adherence.days || null,
      alerts: participantAlerts(row),
    })) };
}
export function csvCell(value: unknown) {
  const raw = value == null ? '' : String(value);
  const text = typeof value === 'string' && /^[\s]*[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
  return `"${text.replace(/"/g, '""')}"`;
}
export function coverageCsv(report: ReturnType<typeof analysisReport>) {
  const rows: unknown[][] = [['Participant ID', 'Name', 'Metric', 'Recorded days', 'Checked empty days', 'Unchecked days', 'Coverage percent', 'First date', 'Last date', 'Read status']];
  for (const row of report.participants) {
    if (!row.coverage) rows.push([row.participant.participantId, row.participant.name, '', '', '', '', '', '', '', row.failed ? 'Unavailable' : 'Pending enrollment']);
    for (const metric of row.coverage || []) rows.push([row.participant.participantId, row.participant.name, metric.metric.dataType,
      metric.recordedDays, metric.emptyDays, metric.unknownDays, metric.coverage, metric.firstDate, metric.lastDate, 'Loaded']);
  }
  return rows.map(row => row.map(csvCell).join(',')).join('\r\n');
}
export function adherenceCsv(report: ReturnType<typeof analysisReport>) {
  const rows: unknown[][] = [['Participant ID', 'Name', 'Date', 'Adherence percent', 'Collection checked', 'Complete day', 'Thresholds', 'Alert status']];
  for (const row of report.participants) for (const day of row.adherence || []) rows.push([row.participant.participantId, row.participant.name,
    day.date, day.adherence, day.checked, day.completeDay, JSON.stringify(day.evaluations),
    row.decisionsUnavailable ? 'Decision unavailable' : row.alerts.find(alert => alert.date === day.date) ? effectiveStatus(row.alerts.find(alert => alert.date === day.date)!.state, report.generatedAt) : '']);
  return rows.map(row => row.map(csvCell).join(',')).join('\r\n');
}
